import React, { useEffect, useState } from 'react';
import api from '../api';

const STATUSES = [
  { value: 'order_received', label: 'Order Received' },
  { value: 'awaiting_materials', label: 'Awaiting Materials' },
  { value: 'in_production', label: 'In Production' },
  { value: 'quality_check', label: 'Quality Check' },
  { value: 'ready_for_dispatch', label: 'Ready for Dispatch' },
  { value: 'delivered', label: 'Delivered' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const EMPTY_FORM = {
  job_number: '', customer_id: null, quote_id: null,
  title: '', description: '', value: '',
  status: 'order_received', priority: 'normal',
  due_date: '', notes: ''
};

function ProjectModal({ project, customers, quotes, onClose, onSaved }) {
  const [form, setForm] = useState(project
    ? { ...project, value: project.value || '' }
    : EMPTY_FORM
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isEdit = !!project;
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleQuoteSelect = (quoteId) => {
    if (!quoteId) {
      set('quote_id', null);
      return;
    }
    const q = quotes.find(q => q.id === parseInt(quoteId));
    if (q) {
      setForm(prev => ({
        ...prev,
        quote_id: q.id,
        customer_id: q.customer_id || null,
        title: q.reference || q.title || q.quote_number || `Quote #${q.id}`,
        value: q.total_value || q.total || q.grand_total || '',
      }));
    }
  };

  const save = async () => {
    if (!form.quote_id) return setError('Please select a quote');
    if (!form.job_number.trim()) return setError('Job number is required');
    if (!form.title.trim()) return setError('Title is required');
    setSaving(true);
    try {
      const payload = {
        ...form,
        quote_id: form.quote_id ? parseInt(form.quote_id) : null,
        customer_id: form.customer_id ? parseInt(form.customer_id) : null,
        value: form.value !== '' ? parseFloat(form.value) : null,
        due_date: form.due_date || null,
      };
      if (isEdit) await api.patch(`/projects/${project.id}`, payload);
      else await api.post('/projects', payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setSaving(false);
    }
  };

  const selectedQuote = quotes.find(q => q.id === form.quote_id);
  const selectedCustomer = customers.find(c => c.id === form.customer_id);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Edit Project' : 'New Project'}</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}

          {/* Quote selector — shown first as it drives other fields */}
          <div className="form-group">
            <label>Quote *</label>
            <select
              value={form.quote_id || ''}
              onChange={e => handleQuoteSelect(e.target.value)}
            >
              <option value="">Select quote...</option>
              {quotes.map(q => (
                <option key={q.id} value={q.id}>
                  {q.reference || q.quote_number || `Quote #${q.id}`}
                  {q.customer_name ? ` — ${q.customer_name}` : ''}
                  {(q.total_value || q.total || q.grand_total)
                    ? ` (£${parseFloat(q.total_value || q.total || q.grand_total).toLocaleString('en-GB', { minimumFractionDigits: 2 })})`
                    : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Auto-populated info panel shown after quote selected */}
          {selectedQuote && (
            <div style={{
              background: 'var(--bg-primary)', border: '1px solid var(--accent)',
              borderRadius: 6, padding: '10px 14px', marginBottom: 16,
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12
            }}>
              <div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 3 }}>Customer</div>
                <div style={{ fontSize: 13 }}>{selectedCustomer?.company_name || selectedQuote.customer_name || '—'}</div>
              </div>
              <div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 3 }}>Value</div>
                <div style={{ fontSize: 13, color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
                  {form.value ? `£${parseFloat(form.value).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '—'}
                </div>
              </div>
              <div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 3 }}>Quote Status</div>
                <div style={{ fontSize: 13 }}>{selectedQuote.status || '—'}</div>
              </div>
            </div>
          )}

          <div className="form-grid">
            <div className="form-group">
              <label>Job Number *</label>
              <input value={form.job_number} onChange={e => set('job_number', e.target.value)} placeholder="e.g. 6252" />
            </div>
            <div className="form-group">
              <label>Priority</label>
              <select value={form.priority} onChange={e => set('priority', e.target.value)}>
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Title *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Skylands Phase 2" />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} />
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Due Date</label>
              <input type="date" value={form.due_date || ''} onChange={e => set('due_date', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectDetail({ project, onClose, onEdit, onDelete }) {
  const status = STATUSES.find(s => s.value === project.status);
  const priority = PRIORITIES.find(p => p.value === project.priority);
  const currentStep = STATUSES.findIndex(s => s.value === project.status);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <span className="modal-title">{project.job_number}</span>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{project.title}</div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">

          {/* Progress tracker */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>Progress</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              {STATUSES.map((s, i) => {
                const done = i < currentStep;
                const active = i === currentStep;
                return (
                  <React.Fragment key={s.value}>
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%', margin: '0 auto 6px',
                        background: done ? 'var(--accent)' : active ? 'var(--accent-glow)' : 'var(--bg-primary)',
                        border: active ? '2px solid var(--accent)' : done ? '2px solid var(--accent)' : '2px solid var(--border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, color: done ? '#000' : active ? 'var(--accent)' : 'var(--text-muted)',
                        fontWeight: 700
                      }}>
                        {done ? '✓' : i + 1}
                      </div>
                      <div style={{ fontSize: 9, color: active ? 'var(--accent)' : done ? 'var(--text-secondary)' : 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: 0.5, lineHeight: 1.3 }}>
                        {s.label.split(' ').map((w, j) => <div key={j}>{w}</div>)}
                      </div>
                    </div>
                    {i < STATUSES.length - 1 && (
                      <div style={{ width: 24, height: 2, background: done ? 'var(--accent)' : 'var(--border)', flexShrink: 0, marginBottom: 24 }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 16 }}>
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Customer</div>
              <div>{project.customer_name || '—'}</div>
            </div>
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Priority</div>
              <span className={`badge badge-${project.priority}`}>{priority?.label}</span>
            </div>
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Due Date</div>
              <div style={{ color: project.due_date && new Date(project.due_date) < new Date() && project.status !== 'delivered' ? 'var(--danger)' : 'var(--text-primary)' }}>
                {project.due_date ? new Date(project.due_date).toLocaleDateString('en-GB') : '—'}
              </div>
            </div>
            {project.value && (
              <div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Value</div>
                <div style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
                  £{parseFloat(project.value).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </div>
            )}
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Created</div>
              <div style={{ color: 'var(--text-secondary)' }}>{project.created_at ? new Date(project.created_at).toLocaleDateString('en-GB') : '—'}</div>
            </div>
            {project.quote_id && (
              <div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Quote</div>
                <div style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', fontSize: 13 }}>#{project.quote_id}</div>
              </div>
            )}
          </div>

          {project.description && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>Description</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.7, background: 'var(--bg-primary)', padding: 12, borderRadius: 4 }}>{project.description}</div>
            </div>
          )}

          {project.notes && (
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>Notes</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.7, background: 'var(--bg-primary)', padding: 12, borderRadius: 4 }}>{project.notes}</div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-danger btn-sm" onClick={onDelete}>Delete</button>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          <button className="btn btn-primary" onClick={onEdit}>Edit</button>
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/projects'), api.get('/customers'), api.get('/quotes')])
      .then(([p, c, q]) => {
        setProjects(p.data.value || []);
        setCustomers(c.data.value || []);
        setQuotes(q.data.value || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = projects.filter(p => {
    const matchSearch = !search ||
      p.job_number?.toLowerCase().includes(search.toLowerCase()) ||
      p.title?.toLowerCase().includes(search.toLowerCase()) ||
      p.customer_name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || p.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    await api.delete(`/projects/${id}`);
    setSelected(null);
    load();
  };

  const statusCounts = STATUSES.map(s => ({
    ...s,
    count: projects.filter(p => p.status === s.value).length
  }));

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">PRO<span>JECTS</span></h2>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>+ New Project</button>
      </div>

      {/* Status summary bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
        {statusCounts.map(s => (
          <div
            key={s.value}
            onClick={() => setStatusFilter(statusFilter === s.value ? '' : s.value)}
            style={{
              background: statusFilter === s.value ? 'var(--accent-glow)' : 'var(--bg-card)',
              border: statusFilter === s.value ? '1px solid var(--accent)' : '1px solid var(--border)',
              borderRadius: 6, padding: '10px 16px', cursor: 'pointer', transition: 'all 0.15s'
            }}
          >
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: statusFilter === s.value ? 'var(--accent)' : 'var(--text-primary)' }}>{s.count}</div>
          </div>
        ))}
      </div>

      <div className="search-bar">
        <input
          placeholder="Search job number, title, customer..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {statusFilter && (
          <button className="btn btn-secondary btn-sm" onClick={() => setStatusFilter('')}>Clear filter ×</button>
        )}
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>
          {filtered.length} projects
        </span>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading">Loading projects...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">No projects found</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Job #</th>
                  <th>Title</th>
                  <th>Customer</th>
                  <th>Value</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Due Date</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const overdue = p.due_date && new Date(p.due_date) < new Date() && p.status !== 'delivered';
                  return (
                    <tr key={p.id} onClick={() => setSelected(p)}>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)' }}>{p.job_number}</td>
                      <td style={{ fontWeight: 600 }}>{p.title}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{p.customer_name || '—'}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)' }}>
                        {p.value ? `£${parseFloat(p.value).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '—'}
                      </td>
                      <td><span className={`badge badge-${p.status}`}>{STATUSES.find(s => s.value === p.status)?.label || p.status}</span></td>
                      <td><span className={`badge badge-${p.priority}`}>{p.priority}</span></td>
                      <td style={{ color: overdue ? 'var(--danger)' : 'var(--text-secondary)', fontSize: 12 }}>
                        {p.due_date ? new Date(p.due_date).toLocaleDateString('en-GB') : '—'}
                        {overdue && <span style={{ marginLeft: 4, fontSize: 10 }}>⚠</span>}
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                        {p.created_at ? new Date(p.created_at).toLocaleDateString('en-GB') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && !editing && (
        <ProjectDetail
          project={selected}
          onClose={() => setSelected(null)}
          onEdit={() => setEditing(selected)}
          onDelete={() => handleDelete(selected.id)}
        />
      )}

      {editing && (
        <ProjectModal
          project={editing}
          customers={customers}
          quotes={quotes}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setSelected(null); load(); }}
        />
      )}

      {showNew && (
        <ProjectModal
          customers={customers}
          quotes={quotes}
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); load(); }}
        />
      )}
    </div>
  );
}